const mouseDragged = (event, provider) => {
    provider.tools.currentTool.dragMouse(event);
}

const isOverUiElement = (provider) => {
    let p = provider.p;
    let rect = p.canvas.getBoundingClientRect();
    let clientX = rect.left + p.mouseX;
    let clientY = rect.top + p.mouseY;
    let el = document.elementFromPoint(clientX, clientY);
    if (!el) return false;
    return el.closest('.planner-ui-overlay') !== null;
};

const onCanvas = (provider) => {
    let p = provider.p;
    return !provider.sideBar.hovered()
        && !isOverUiElement(provider)
        && p.mouseX > 0
        && p.mouseX < p.width
        && p.mouseY > 0
        && p.mouseY < p.height;
}

const releaseMouseOnCanvas = (provider) => {
    return () => {
        if (onCanvas(provider)) {
            provider.tools.currentTool.releaseMouseOnCanvas();
        }
    }
}

const clickMouseOnCanvas = (provider) => {
    return () => {
        if (onCanvas(provider)) {
            provider.tools.currentTool.clickMouseOnCanvas();
        }
    }
}

const pressMouseOnCanvas = (provider) => {
    return () => {
        if (onCanvas(provider)) {
            provider.tools.currentTool.pressMouseOnCanvas();
        }
    }
}

const doubleClickedOnCanvas = (provider) => {
    return (event) => {
        if (onCanvas(provider)) {
            const consumed = provider.tools.currentTool.doubleClickedOnCanvas();
            if (!consumed) {
                provider.view.zoomIn(provider.p.mouseX, provider.p.mouseY, 1.5);
            }
        }
    }
}

const mouseMoved = (provider) => {
    // It can happen that the mouseMoved event is triggered before the provider is initialized.
    if (!provider) {
        return;
    }
    if (onCanvas(provider)) {
        provider.tools.currentTool?.moveMouse(provider);
    }
}

const mouseWheel = (event, provider) => {
    const modalOpen = Array.from(document.querySelectorAll('.modal-overlay'))
        .some(el => el.style.display !== 'none');
    if (provider.tools.currentTool.scrollEnabled && !provider.sideBar.hovered() && !modalOpen) {
        event.preventDefault();
        if (provider.p.keyIsDown(provider.p.CONTROL)) {
            provider.view.move(0, event.delta / 8);
        } else if (provider.p.keyIsDown(provider.p.SHIFT)) {
            provider.view.move(event.delta / 8, 0);
        } else {
            if (event.delta < 0) {
                provider.view.zoomIn(provider.p.mouseX, provider.p.mouseY, 1.1);
            } else {
                provider.view.zoomOut(provider.p.mouseX, provider.p.mouseY, 1.1);
            }
        }
        return false; // disable normal scroll behavior
    }
}

const registerMouseHandlers = (p, provider) => {
    provider.canvas
        .mouseReleased(releaseMouseOnCanvas(provider))
        .mouseClicked(clickMouseOnCanvas(provider))
        .mousePressed(pressMouseOnCanvas(provider))
        .doubleClicked(doubleClickedOnCanvas(provider));

    p.mouseDragged = (event) => {
        if (onCanvas(provider)) {
            mouseDragged(event, provider);
        }
    }

    p.mouseMoved = () => {
        mouseMoved(provider);
    }

    // When pointerup fires on an HTML element, p5 never sees it and keeps mouseIsPressed=true,
    // causing all subsequent mouse movement to fire mouseDragged instead of mouseMoved.
    // Reset p5's internal state and run the tool release handler.
    document.addEventListener('pointerup', (e) => {
        if (e.target !== provider.canvas.elt) {
            p.mouseIsPressed = false;
            p._mouseIsPressed = false;
        }
    });

    p.mouseWheel = (event) => {
        mouseWheel(event, provider);
    }
}

const registerTouchHandlers = (p, provider) => {
    let lastTouchTime = 0;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let lastPinchDistance = 0;
    let longPressTimer = null;
    let longPressTriggered = false;
    let singleTouchActive = false;
    let singleTouchPending = false; // waiting out the 2-finger grace period
    let pressTimer = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let dragActivated = false;

    const commitSingleTouch = () => {
        singleTouchPending = false;
        singleTouchActive = true;
        pressMouseOnCanvas(provider)();
        longPressTimer = setTimeout(() => {
            longPressTimer = null;
            longPressTriggered = true;
            provider.p.mouseButton = provider.p.RIGHT;
            pressMouseOnCanvas(provider)();
            provider.p.mouseButton = provider.p.LEFT;
        }, 500);
    };

    p.touchStarted = (event) => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }

        if (p.touches.length === 2) {
            // Second finger arrived — cancel any pending single-touch action
            if (pressTimer) {
                clearTimeout(pressTimer);
                pressTimer = null;
            }
            singleTouchPending = false;
            singleTouchActive = false;

            lastTouchX = (p.touches[0].winX + p.touches[1].winX) / 2;
            lastTouchY = (p.touches[0].winY + p.touches[1].winY) / 2;
            lastPinchDistance = Math.hypot(
                p.touches[1].winX - p.touches[0].winX,
                p.touches[1].winY - p.touches[0].winY
            );
            return false;
        }

        if (p.touches.length !== 1) return;
        if (event.target !== provider.canvas.elt) return;

        const now = Date.now();
        if (now - lastTouchTime < 300) {
            lastTouchTime = 0;
            doubleClickedOnCanvas(provider)();
            return false;
        }

        lastTouchTime = now;
        touchStartX = p.touches[0].winX;
        touchStartY = p.touches[0].winY;
        dragActivated = false;
        singleTouchPending = true;

        // Delay committing to a single-touch action to allow a second finger to land
        pressTimer = setTimeout(() => {
            pressTimer = null;
            if (singleTouchPending) {
                commitSingleTouch();
            }
        }, 100);

        return false;
    }

    p.touchMoved = (event) => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }

        if (p.touches.length === 2) {
            const cx = (p.touches[0].winX + p.touches[1].winX) / 2;
            const cy = (p.touches[0].winY + p.touches[1].winY) / 2;
            const dx = cx - lastTouchX;
            const dy = cy - lastTouchY;
            lastTouchX = cx;
            lastTouchY = cy;
            provider.view.move(dx, dy);

            const pinchDistance = Math.hypot(
                p.touches[1].winX - p.touches[0].winX,
                p.touches[1].winY - p.touches[0].winY
            );
            const scale = pinchDistance / lastPinchDistance;
            lastPinchDistance = pinchDistance;
            if (Math.abs(scale - 1) > 0.05) {
                if (scale > 1) {
                    provider.view.zoomIn(cx, cy, scale);
                } else {
                    provider.view.zoomOut(cx, cy, 1 / scale);
                }
            }

            return false;
        }

        if (p.touches.length !== 1 || !singleTouchActive) return;

        if (!dragActivated) {
            const dist = Math.hypot(p.touches[0].winX - touchStartX, p.touches[0].winY - touchStartY);
            if (dist < 8) return false;
            dragActivated = true;
        }

        provider.tools.currentTool.dragMouse(event);
        return false;
    }

    p.touchEnded = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }

        // Finger lifted before the press delay resolved — treat as a quick tap
        if (pressTimer) {
            clearTimeout(pressTimer);
            pressTimer = null;
            if (singleTouchPending && p.touches.length === 0) {
                singleTouchPending = false;
                pressMouseOnCanvas(provider)();
                releaseMouseOnCanvas(provider)();
                clickMouseOnCanvas(provider)();
            }
            return false;
        }

        if (!singleTouchActive) return;
        if (p.touches.length > 0) return false;

        singleTouchActive = false;

        if (longPressTriggered) {
            longPressTriggered = false;
            releaseMouseOnCanvas(provider)();
            return false;
        }

        releaseMouseOnCanvas(provider)();
        clickMouseOnCanvas(provider)();
        return false;
    }
}

export { mouseDragged, mouseMoved, mouseWheel, clickMouseOnCanvas, doubleClickedOnCanvas, pressMouseOnCanvas, releaseMouseOnCanvas, onCanvas, registerMouseHandlers, registerTouchHandlers };
